/**
 * @file grn-list.tsx
 * @deprecated Copied to app/modules/procurement/pages/goods-received-note/grn-list.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/goods-received-note/grn-list
 * @description Goods Received Notes list view with paginated table, advanced filtering
 *   (date range, warehouse, supplier), and search functionality. Displays GRN metadata
 *   including PO reference, supplier, received quantities, and status. Supports navigation
 *   to detail views and add-to-inventory workflow.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo [P1] Implement API data fetching:
 *   - Replace static table data with fetchGRNList() API call
 *   - Support pagination (page, limit, total)
 *   - Add search functionality via debounced API call
 *   - Implement warehouse filter to show GRNs per warehouse
 *   - Reference: purchase-order-list.tsx data fetching pattern
 *
 * @todo [P1] Add date range filtering:
 *   - Implement handleDateFilter() for GRN created date range
 *   - Store dates in session storage via getKey/setKey pattern
 *   - Re-fetch list on date change
 *   - Reference: purchase-order-list.tsx date filter implementation
 *
 * @todo [P2] Add column definitions for react-table:
 *   - GRN ID (reference key)
 *   - Supplier / Vendor name with image
 *   - PO Reference linked to PO details
 *   - Received Date
 *   - Total Received Quantity
 *   - Rejected Quantity
 *   - Status badge (Pending, Completed, Partially Received)
 *   - Actions (view details, edit if pending, add to inventory)
 *
 * @todo [P2] Add supplier/warehouse multi-select filters:
 *   - Implement supplier dropdown with search and pagination
 *   - Add warehouse selection dropdown
 *   - Support combined filtering (supplier + warehouse + date)
 *   - Store filter state in session for page recovery
 *
 * @todo [P2] Add error handling and retry logic:
 *   - Try/catch blocks around API calls
 *   - Show error toasts for fetch failures
 *   - Reset loading flags in finally blocks
 *   - Implement retry button on network errors
 *
 * @todo [P3] Add export/batch action functionality:
 *   - Export visible GRNs as CSV/PDF
 *   - Bulk mark as completed for multiple GRNs
 *   - Bulk add to inventory action
 */

/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { getKey, setKey, removeKey } from "../../../../Global/history";
import {
  Admin,
  GoodsReceivedNote as GoodsReceivedNoteModule,
  PAGE_LIMIT,
  PermissionModule,
  Permissions,
} from "../../../../utils/constants";
import Method from "../../../../utils/methods";
import { listGoodsReceivedNotes } from "../../../../utils/storeString";
import { useDebounce } from "../../../../utils/useDebounce";
import { CustomTable } from "../../../custom/data-table";
import CustomDatePicker from "../../../custom/DateRange/CustomDatePicker";
import DynamicListLayout from "../../../custom/layouts/DynamicListLayout";
import { CustomSearchInput } from "../../../custom/search";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import APICallService from "../../../../api/apiCallService";
import { manageProduct, vendor, PURCHASEORDER } from "../../../../api/apiEndPoints";
import { useAuth } from "../../auth";
import { ElementAnimateUtil } from "../../../../_admin/assets/ts/_utils";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useNavigationHistory } from "../../../../utils/useNavigationHistory";
import editIcon from "../../../../_admin/assets/media/svg_uTrade/edit-round-blue.svg";
import warehouseIcon from "../../../../_admin/assets/media/dashboard/warehouse.svg";
import LegacyGrnNotice from "../../inward/components/legacy-grn-notice";

interface QuantityType {
  type: number;
  stockCount: number;
  receivedStockCount: number;
  rejectedStockCount: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
    weight: number;
  };
  amount?: number;
  discountAmt?: number;
  purchaseAmount?: number;
}

interface Variant {
  product: string;
  variant: string;
  clonedRef: string;
  quantityTypes: QuantityType[];
}

interface GoodsReceivedNote {
  _id: string;
  refKey: string;
  shipment: any;
  status: number;
  date: string;
  receivedBy?: string;
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
  gateEntryNumber?: string;
  invoiceNumber?: string;
  images?: string[];
  notes?: string;
  variants?: Variant[];
  _createdAt?: string;
  createdAt?: string;
  updatedAt?: string;
  inventoryAdjustmentExists?: boolean;
  priceAddPending?: boolean;
}

type GrnActionFilter = "priceAdd" | "addToInventory" | "";

const grnActionFilterOptions = [
  {
    label: "Pending Price Update",
    value: "priceAdd",
    title: "Pending Price Update",
  },
  {
    label: "Pending Inventory Update",
    value: "addToInventory",
    title: "Pending Inventory Update",
  },
];

const GRNList = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { navigateTo } = useNavigationHistory();
  const { selectedWarehouse } = useWarehouse();
  const [goods, setGoods] = useState<GoodsReceivedNote[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const { state }: any = useLocation();
  const [page, setPage] = useState<any>(
    getKey(listGoodsReceivedNotes.page) || 1
  );
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listGoodsReceivedNotes.limit) || PAGE_LIMIT
  );
  const [startDate, setStartDate] = useState<any>(() => {
    const savedDate = getKey(listGoodsReceivedNotes.filterStartDate);
    return savedDate ? new Date(savedDate.toString()) : null;
  });
  const [endDate, setEndDate] = useState<any>(() => {
    const savedDate = getKey(listGoodsReceivedNotes.filterEndDate);
    return savedDate ? new Date(savedDate.toString()) : null;
  });
  const [dataFetched, setDataFetched] = useState(false);
  const [search, setSearch] = useState<string>(() => {
    const savedSearch = getKey(listGoodsReceivedNotes.search);
    return savedSearch ? String(savedSearch) : "";
  });
  const [actionFilter, setActionFilter] = useState<GrnActionFilter>(() => {
    const savedFilter = getKey(listGoodsReceivedNotes.filterAction);

    if (savedFilter === "priceAdd" || savedFilter === "addToInventory") {
      return savedFilter;
    }

    return "";
  });
  const [tabKey, setTabKey] = useState(
    getKey(listGoodsReceivedNotes.currentTab) || state || 1
  );
  const [vendorData, setVendorData] = useState<any[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<any>(() => {
    const v = getKey(listGoodsReceivedNotes.filterVendor);
    return v ? v : undefined;
  });
  const [selectedPO, setSelectedPO] = useState<any>(() => {
    const p = getKey(listGoodsReceivedNotes.filterPO);
    return p ? p : undefined;
  });
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);

  const canManageGrnActions = // eslint-disable-line
    currentUser?.userType === Admin ||
    currentUser?.roleAndPermission?.isWarehouseManagerRole ||
    currentUser?.roleAndPermission?.roleType === "R0012" ||
    currentUser?.roleAndPermission?.roleType === "R0003";

  // // Helper function to get quantity type label
  // const getQuantityTypeLabel = (type: number): string => {
  //   switch (type) {
  //     case 1: return 'CartonWithDozens';
  //     case 2: return 'CartonWithPieces';
  //     case 3: return 'Dozen';
  //     case 4: return 'Piece';
  //     default: return '';
  //   }
  // };

  const handleRowClick = (order: GoodsReceivedNote) => {
    let currentScrollY = window.scrollY;
    setKey(listGoodsReceivedNotes.scrollPosition, currentScrollY);
    setKey(listGoodsReceivedNotes.search, search, false);
    setKey(listGoodsReceivedNotes.filterStartDate, startDate, false);
    setKey(listGoodsReceivedNotes.filterEndDate, endDate, false);
    navigateTo(
      "/UProcure/purchase-order-management/purchase-order-details",
      {
        state: {
          id: order.shipment._id,
        },
      }
    );
  };

  const columns: ColumnDef<GoodsReceivedNote>[] = [
    {
      header: "GRN Number",
      accessorKey: "refKey",
      cell: ({ getValue }) => <span>GRN-{getValue() as string}</span>,
    },
    {
      header: "PO No",
      accessorKey: "shipment.refKey",
      cell: ({ getValue }) => <span>{(getValue() as string) || "N/A"}</span>,
    },
    {
      header: "Supplier Name",
      accessorKey: "shipment.business.ownerName",
      cell: ({ getValue }) => <span>{(getValue() as string) || "N/A"}</span>,
    },
    {
      header: "Driver Name",
      accessorKey: "driverName",
      cell: ({ getValue }) => <span>{(getValue() as string) || "N/A"}</span>,
    },
    {
      header: "Vehicle Number",
      accessorKey: "vehicleNumber",
      cell: ({ getValue }) => <span>{(getValue() as string) || "N/A"}</span>,
    },
    {
      header: "Received Quantity",
      cell: ({ row }) => {
        const order = row.original;
        if (!order.variants || order.variants.length === 0) {
          return <span>-</span>;
        }

        // Aggregate receivedStockCount by quantity type across all variants
        const typeTotals: Record<number, number> = {};
        order.variants?.forEach((v) =>
          v.quantityTypes?.forEach((qt) => {
            const t = Number(qt.type);
            const cnt = Number(qt.receivedStockCount) || 0;
            typeTotals[t] = (typeTotals[t] || 0) + cnt;
          })
        );

        const quantities = Object.keys(typeTotals)
          .map((k) => {
            const t = Number(k);
            return Method.formatQuantityDisplay(t, typeTotals[t]);
          })
          .filter(Boolean);

        return <span>{quantities.join(", ") || "-"}</span>;
      },
    },
    {
      header: "Delivery Date & Time",
      accessorKey: "date",
      cell: ({ getValue }) => (
        <span>
          {Method.convertDateToDDMMYYYYHHMMAMPMWithSeperator(
            getValue() as string,
            "-"
          )}
        </span>
      ),
    },
      ...(currentUser?.userType === Admin ||
      currentUser?.roleAndPermission?.isWarehouseManagerRole || currentUser?.roleAndPermission?.roleType === "R0012" || currentUser?.roleAndPermission?.roleType === "R0003"
      || Method.hasPermission(PermissionModule.GoodsReceivedNote,Permissions.Edit,currentUser)
      ?
    [
    {
      header: "Actions",
      cell: ({ row }: any) => {
        const order = row.original;
        console.log(row);
        const persistListState = () => {
          let currentScrollY = window.scrollY;
          setKey(listGoodsReceivedNotes.scrollPosition, currentScrollY);
          setKey(listGoodsReceivedNotes.search, search, false);
          setKey(listGoodsReceivedNotes.filterStartDate, startDate, false);
          setKey(listGoodsReceivedNotes.filterEndDate, endDate, false);
        }
        return (
          <div className="text-nowrap">
            {order.priceAddPending ? (
              <Button className="uw-btn uw-btn-primary"
                variant="primary"
                style={{
                  border: "none",
                  padding: "6px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  persistListState();
                  navigateTo(
                    "/UProcure/purchase-order-management/purchase-order-details",
                    {
                      state: {
                        id: order.shipment._id,
                        openDeliveryRefKey: order.refKey,
                      },
                    }
                  );
                }}
              >
                <img
                  src={editIcon}
                  alt="add price"
                  style={{ width: "30px", height: "30px" }}
                />
              </Button>
            ) : order.inventoryAdjustmentExists ? (
              <Button className="uw-btn uw-btn-danger"
                variant="danger"
                style={{
                  backgroundColor: "#dc3545",
                  border: "none",
                  padding: "8px 10px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  persistListState();
                  navigate(
                    "/UProcure/goods-received-note/add-goods-to-inventory",
                    {
                      state: {
                        id: order._id,
                        status: order.status,
                        shipment: order.shipment._id,
                        refKey: order.refKey,
                      },
                    }
                  );
                }}
              >
                <img
                  src={warehouseIcon}
                  alt="warehouse"
                  style={{ width: "20px", height: "20px", filter: "brightness(0) invert(1)" }}
                />
              </Button>
            ) : (<OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`goods-received-tooltip-${order._id}`}>
                    <p className="text-white mb-0 fs-14 fw-500">
                      Goods received
                    </p>
                  </Tooltip>
                }
              >
                <span className="d-inline-block">
                  <Button className="uw-btn uw-btn-success"
                    disabled={true}
                    variant="success"
                    style={{
                      backgroundColor: "#28a745",
                      border: "none",
                      padding: "8px 10px",
                      pointerEvents: "none",
                    }}
                  >
                    <img
                      src={warehouseIcon}
                      alt="warehouse"
                      style={{
                        width: "20px",
                        height: "20px",
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                  </Button>
                </span>
              </OverlayTrigger>)}
          </div>
        );
      },
    }]:[]),
  ];

  // Map tab to status values
  const getStatusByTab = (tab: number): number[] => {
    switch (tab) {
      case 1:
        return [1]; // Pending
      case 2:
        return [2]; // Completed
      case 3:
        return [3]; // Partial
      default:
        return [];
    }
  };

  const fetchGoodsReceivedNotes = async (
    pageNo: number,
    limit: number,
    tab: number,
    searchTerm?: string,
    fromDate?: string,
    toDate?: string,
    businessId?: string,
    poId?: string,
    selectedActionFilter: GrnActionFilter = ""
  ) => {
    try {
      setLoading(true);

      let params: any = {
        sortKey: "_createdAt",
        sortOrder: -1,
        needCount: true,
        skip: (pageNo - 1) * limit,
        limit: limit,
        ...(businessId && { businessId: businessId }),
        ...(poId && { shipment: poId }),
      };

      // Add search term if provided
      if (searchTerm?.trim()) {
        params.searchTerm = searchTerm.replace(/^GRN-/i, "");
      }

      if (selectedWarehouse?.value) {
        params.warehouse = selectedWarehouse.value;
      }

      // Add status filter based on tab
      const statusArr = getStatusByTab(tab);
      if (statusArr.length) {
        statusArr.forEach((val, index) => {
          params[`status[${index}]`] = val;
        });
      }

      // Add date filters if provided
      if (fromDate) {
        params.fromDate = Method.convertDateToFormat(fromDate, "YYYY-MM-DD");
      }
      if (toDate) {
        params.toDate = Method.convertDateToFormat(toDate, "YYYY-MM-DD");
      }

      if (selectedActionFilter) {
        params.actionPending = selectedActionFilter;
      }

      setGoods([]);
      setTotalRecords(0);

      // API endpoint from your reference
      const apiService = new APICallService(
        manageProduct.shipmentTracking,
        params,
        "",
        "",
        "",
        "",
        GoodsReceivedNoteModule,
        selectedWarehouse?.value
      );

      const response = await apiService.callAPI();

      if (response) {
        setGoods(response?.records || []);
        setTotalRecords(response?.total || 0);
      }
    } catch (error) {
      console.error("GRN list API failed", error);
    } finally {
      setDataFetched(true);
      setLoading(false);
    }
  };

  const fetchVendorsList = async () => {
    try {
      let params: any = {
        "status[]": 4,
      };

      const apiService = new APICallService(
        vendor.listVendor,
        params,
        "",
        "",
        "",
        "",
        Admin
      );

      const response = await apiService.callAPI();

      if (response) {
        setVendorData(response?.records || []);
      }
    } catch (error) {
      console.error("Vendor list API failed", error);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      let params: any = {
        sortKey: "_createdAt",
        sortOrder: -1,
        needCount: false,
        "status[0]": 2,
        "status[1]": 9,
        "status[2]": 6, // In Progress, Completed and Partially Completed
      };
      let apiService = new APICallService(PURCHASEORDER.listPurchaseOrders, params);
      let response = await apiService.callAPI();
      if (response?.records) {
        setPurchaseOrders(response?.records);
      } else {
        setPurchaseOrders([]);
      }
    } catch (error) {
      console.error("Purchase orders API failed", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
    fetchVendorsList();
    fetchGoodsReceivedNotes(
      page,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString(),
      selectedVendor,
      selectedPO,
      actionFilter
    );
  }, [page, pageLimit, tabKey, actionFilter, selectedVendor, selectedPO, search, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dataFetched) {
      let initialScrollPosition: any = getKey(listGoodsReceivedNotes.scrollPosition);
      if (initialScrollPosition) {
        ElementAnimateUtil.scrollTo(parseInt(initialScrollPosition));
        setDataFetched(false);
      }
    }
  }, [dataFetched]);

  // Clean up malformed search values on mount
  useEffect(() => {
    const savedSearch = getKey(listGoodsReceivedNotes.search);
    if (savedSearch) {
      const searchStr =
        typeof savedSearch === "string" ? savedSearch : String(savedSearch);
      const trimmed = searchStr.trim();
      if (
        trimmed === '""' ||
        trimmed === '"""' ||
        trimmed === '"' ||
        (trimmed.match(/["\\|]/g)?.length &&
          trimmed.match(/["\\|]/g)!.length / trimmed.length > 0.3)
      ) {
        removeKey(listGoodsReceivedNotes.search);
        setSearch("");
      }
    }
  }, []);

  const tabs = [
    {
      label: "Pending",
      value: 1,
    },
    {
      label: "Completed",
      value: 2,
    },
    {
      label: "Partial",
      value: 3,
    },
  ];

  const handleTabChange = async (value: string | number) => {
    const numericValue = typeof value === "string" ? parseInt(value) : value;
    setTabKey(numericValue);
    setPage(1);
    setKey(listGoodsReceivedNotes.page, 1);
    setKey(listGoodsReceivedNotes.currentTab, numericValue);
    await fetchGoodsReceivedNotes(
      1,
      pageLimit,
      numericValue,
      search,
      startDate?.toISOString(),
      endDate?.toISOString(),
      actionFilter
    );
  };

  const debounce = useDebounce(fetchGoodsReceivedNotes, 300);

  const handleSearch = async (input: string) => {
    setSearch(input);
    setPage(1);
    setKey(listGoodsReceivedNotes.page, 1);
    setKey(listGoodsReceivedNotes.search, input, false);

    await debounce(
      1,
      pageLimit,
      tabKey,
      input,
      startDate?.toISOString(),
      endDate?.toISOString(),
      selectedVendor,
      selectedPO,
      actionFilter
    );
  };

  const handleActionFilter = (selectedOption: any) => {
    const selectedValue: GrnActionFilter =
      selectedOption?.value === "priceAdd" ||
      selectedOption?.value === "addToInventory"
        ? selectedOption.value
        : "";

    setActionFilter(selectedValue);
    setPage(1);
    setKey(listGoodsReceivedNotes.page, 1);

    if (selectedValue) {
      setKey(listGoodsReceivedNotes.filterAction, selectedValue, false);
    } else {
      removeKey(listGoodsReceivedNotes.filterAction);
    }
  };

  const handleVendor = async (event: any) => {
    const v = event?.value ?? "";
    setSelectedVendor(v || undefined);
    setKey(listGoodsReceivedNotes.filterVendor, v, false);
    setPage(1);
    setKey(listGoodsReceivedNotes.page, 1);
    await fetchGoodsReceivedNotes(
      1,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString(),
      v,
      selectedPO,
      actionFilter
    );
  };

  const handlePO = async (event: any) => {
    const p = event?.value ?? "";
    setSelectedPO(p || undefined);
    setKey(listGoodsReceivedNotes.filterPO, p || "", false);
    setPage(1);
    setKey(listGoodsReceivedNotes.page, 1);
    await fetchGoodsReceivedNotes(
      1,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString(),
      selectedVendor,
      p,
      actionFilter
    );
  };

 const handleDateFilter = async (event: any) => {
  const start = event?.[0];
  const end = event?.[1];
  console.log("call date==>")

  setStartDate(start);
  setEndDate(end);
  setTotalRecords(0);
  setPage(1);
  setKey(listGoodsReceivedNotes.page, 1);

  // If both dates selected
  if (start && end) {
    const startDateFormatted = Method.convertDateToFormat(start, "YYYY-MM-DD");
    const endDateFormatted = Method.convertDateToFormat(end, "YYYY-MM-DD");

    setKey(listGoodsReceivedNotes.filterStartDate, start, false);
    setKey(listGoodsReceivedNotes.filterEndDate, end, false);

    await fetchGoodsReceivedNotes(
      1,
      pageLimit,
      tabKey,
      search,
      startDateFormatted,
      endDateFormatted,
      selectedVendor,
      selectedPO,
      actionFilter
    );
  }

  if(!start && !end){
    // When datepicker is cleared
    removeKey(listGoodsReceivedNotes.filterStartDate);
    removeKey(listGoodsReceivedNotes.filterEndDate);

    await fetchGoodsReceivedNotes(
      1,
      pageLimit,
      tabKey,
      search,
      "",
      "",
      selectedVendor,
      selectedPO,
      actionFilter
    );
  }
};

  const handleCurrentPage = async (val: number) => {
    if (val === page || val.toString() === "...") return;
    setPage(val);
    setKey(listGoodsReceivedNotes.page, val);
  };

  const handleNextPage = async () => {
    const newPage = Math.min(page + 1, Math.ceil(totalRecords / pageLimit));
    setPage(newPage);
    setKey(listGoodsReceivedNotes.page, newPage);
  };

  const handlePreviousPage = async () => {
    const newPage = Math.max(page - 1, 1);
    setPage(newPage);
    setKey(listGoodsReceivedNotes.page, newPage);
  };

  const handlePageLimit = async (event: any) => {
    const limit = parseInt(event.target.value);
    setPage(1);
    setPageLimit(limit);
    setKey(listGoodsReceivedNotes.page, 1);
    setKey(listGoodsReceivedNotes.limit, limit);
  };

  return (
    <>
      <LegacyGrnNotice inwardPath="/UProcure/inward/grns" />
      <DynamicListLayout
        title="Goods Received Note"
        tabs={tabs}
        activeTab={tabKey}
        onTabChange={handleTabChange}
        variant="withoutTabs"
        // action={
        //   <Button
        //     onClick={() => {
        //       navigate("/UProcure/goods-received-note/add-grn");
        //       setKey(listGoodsReceivedNotes.search, search, false);
        //       setKey(listGoodsReceivedNotes.filterStartDate, startDate, false);
        //       setKey(listGoodsReceivedNotes.filterEndDate, endDate, false);
        //     }}
        //   >
        //     Add GRN
        //   </Button>
        // }
        filters={
          <>
            <Row className="align-items-center g-3 justify-content-end">
              <Col xs={12} md={4}>
                <CustomSearchInput
                  defaultValue={search}
                  onSearch={(value: any) => handleSearch(value)}
                  placeholder="Search by grn no, driver name, vehicle no, PO no, supplier name"
                />
              </Col>
              <Col xs={12} md={3}>
                <CustomDatePicker
                  onChange={handleDateFilter}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  showFullMonthYearPicker={true}
                  placeholderText="Select dates"
                  maxDate={new Date()}
                  isClearable={true}
                  inputTextBG="bg-white"
                />
              </Col>
              <Col xs={12} md={3}>
                <div className="min-w-280px max-w-280px">
                  <CustomSelect
                    value={
                      grnActionFilterOptions.find(
                        (option) => option.value === actionFilter
                      ) || ""
                    }
                    options={grnActionFilterOptions}
                    onChange={handleActionFilter}
                    placeholder="Filter by pending action"
                    backgroundColor="#f9f9f9"
                    minHeight="58px"
                    isClearable={true}
                  />
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="min-w-280px max-w-280px">
                  <CustomSelect
                    value={
                      vendorData.find((option) => option?._id === selectedVendor) || ""
                    }
                    options={vendorData.map((item: any) => ({
                      _id: item?._id,
                      value: item?._id,
                      label: item?.businessName || item?.name || "N/A",
                    }))}
                    onChange={handleVendor}
                    placeholder="Filter by Vendor"
                    backgroundColor="#f9f9f9"
                    minHeight="58px"
                    isClearable={true}
                  />
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div className="min-w-280px max-w-280px">
                  <CustomSelect
                    value={
                      purchaseOrders.find((option) => option?._id === selectedPO) || ""
                    }
                    options={purchaseOrders.map((item: any) => ({
                      _id: item?._id,
                      value: item?._id,
                      label: item?.poNumber || item?.refKey || "N/A",
                    }))}
                    onChange={handlePO}
                    placeholder="Filter by Purchase Order"
                    backgroundColor="#f9f9f9"
                    minHeight="58px"
                    isClearable={true}
                  />
                </div>
              </Col>
            </Row>
          </>
        }
        totalRecords={totalRecords}
        page={page}
        pageLimit={100}
        handleCurrentPage={handleCurrentPage}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
        handlePageLimit={handlePageLimit}
      >
        <CustomTable columns={columns} data={goods} loading={loading} onRowClick={(order: GoodsReceivedNote) => {
            handleRowClick(order);
          }} />
      </DynamicListLayout>
    </>
  );
};

export default GRNList;
